import React, { useEffect, useState } from "react";
import {
  Button,
  FormGroup,
  Grid,
  Popover,
  Box,
  Paper,
  InputBase,
  IconButton,
  Tooltip,
  Stack,
} from "../../../common/utils/mui-imports";
import HPRCheckBox from "../HPRCheckBox/HPRCheckBox";
import {
  ArrowDown,
  ArrowSwapVertical,
  ArrowUp,
  Filter,
  SearchNormal1,
} from "iconsax-react";
import { Close as CloseIcon } from "../../../common/utils/mui-imports";
import { SiteDTO } from "../../../common/types/common.dto.types";
import { EnumSortTypes } from "../../../common/enums/common.enums";
import TooltipScrollContainer from "../TooltipScrollContainer/TooltipScrollContainer";

interface InspectionOverviewFilterSectionProps {
  inspectionLocations?: SiteDTO[];
  inspectionLocationIds?: number[];
  showCompleted: boolean;
  sortState: number;
  onApplyLocationFilters?: (selectedLocations: number[]) => void;
  onShowCompletedChange?: (isChecked: boolean) => void;
  onPlannedCompletionDateSortChange?: (number) => void;
}

const InspectionOverviewFilterSection: React.FC<
  InspectionOverviewFilterSectionProps
> = ({
  inspectionLocations,
  inspectionLocationIds,
  showCompleted,
  sortState,
  onApplyLocationFilters,
  onShowCompletedChange,
  onPlannedCompletionDateSortChange,
}) => {
  // Popover state

  const getSortStateIcon = () => {
    switch (sortState) {
      case EnumSortTypes.Ascending:
        return <ArrowUp size="20" color="#ff6c24" />;
      case EnumSortTypes.Descending:
        return <ArrowDown size="20" color="#ff6c24" />;
      default:
        return <ArrowSwapVertical variant="Outline" size={20} />;
    }
  };

  const getSortTooltipText = () => {
    switch (sortState) {
      case EnumSortTypes.Ascending:
        return "Sorted by Ascending (A→Z)";
      case EnumSortTypes.Descending:
        return "Sorted by Descending  (Z→A)";
      default:
        return "Click to Sort";
    }
  };

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const [isMouseOver, setIsMouseOver] = useState(false);

  const [filteredLocations, setFilteredLocations] = useState<SiteDTO[]>([]);

  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);

  /**
   * @name onChangeSearchName
   * @param searchValue
   * @description Filters the inspection locations based on the search value.
   * @returns void
   */
  const onChangeSearchName = (searchValue: string) => {
    setSearchTerm(searchValue);
    if (!searchValue || searchValue === "") {
      setFilteredLocations(inspectionLocations);
    }
  };

  /**
   * @description Filters the inspection locations based on the search term.
   */
  useEffect(() => {
    setFilteredLocations(
      inspectionLocations.filter((loc) =>
        loc.SiteName?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [inspectionLocations]);

  /**
   * @description Sets the selected inspection Ids if any.
   */
  useEffect(() => {
    setSelectedLocationIds(inspectionLocationIds);
  }, [inspectionLocationIds, anchorEl]);

  // Popover handlers
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    onChangeSearchName("");
    setAnchorEl(null);
  };

  /**
   * @name handlePlannedCompletionDateSort
   * @description Handles the sort of planned completion date.
   * @returns void
   */
  const handlePlannedCompletionDateSort = () => {
    let sortValue = EnumSortTypes.Default;
    if (sortState === EnumSortTypes.Default)
      sortValue = EnumSortTypes.Ascending;
    else if (sortState === EnumSortTypes.Ascending)
      sortValue = EnumSortTypes.Descending;

    if (onPlannedCompletionDateSortChange) {
      onPlannedCompletionDateSortChange(sortValue);
    }
  };

  // Search handlers
  const handleMouseEnter = () => setIsMouseOver(true);
  const handleMouseLeave = () => setIsMouseOver(false);

  /**
   * @name onKeyPressSearchName
   * @description Handles the key press event for the search input.
   * @param event
   * @returns void
   */
  const onKeyPressSearchName = (event: React.KeyboardEvent) => {
    if (event.charCode === 13) {
      event.preventDefault();
      onClickSearchBtn();
    }
  };

  /**
   * @name onClickSearchBtn
   * @description Filters the inspection locations based on the search term when the search button is clicked.
   * @returns void
   */
  const onClickSearchBtn = () => {
    setFilteredLocations(
      inspectionLocations.filter((loc) =>
        loc.SiteName?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };

  /**
   * @name handleLocationSelection
   * @description Handles the selection or deselection of a location.
   * @param locationId
   * @param checked
   * @returns void
   */
  const handleLocationSelection = (locationId: number, checked: boolean) => {
    if (checked && !selectedLocationIds.includes(locationId)) {
      setSelectedLocationIds([...selectedLocationIds, locationId]);
    } else if (!checked && selectedLocationIds.includes(locationId)) {
      setSelectedLocationIds(
        selectedLocationIds.filter((id) => id !== locationId)
      );
    }
  };

  /**
   * @name onSelectAllClick
   * @description Selects all inspection locations.
   * @returns void
   */
  const onSelectAllClick = () => {
    setSelectedLocationIds(inspectionLocations.map((loc) => loc.Id));
  };

  /**
   * @name onDeselectAllClick
   * @description Deselects all inspection locations.
   * @returns void
   */
  const onDeselectAllClick = () => {
    setSelectedLocationIds([]);
  };

  /**
   * @name checkIfLocationIsSelected
   * @description Checks if a location is selected based on its ID.
   * @param locationId
   * @returns boolean indicating if the location is selected
   */
  const checkIfLocationIsSelected = (locationId: number) => {
    return selectedLocationIds.includes(locationId);
  };

  return (
    <div className="inspection-overview-filter-section pl-10">
      <Grid item xl={4} lg={4} md={4} sm={12} xs={12} className="checkbox-grid">
        <Grid>
          <HPRCheckBox
            Id=""
            Name="Include Completed Inspections"
            Label="Include Completed Inspections"
            LabelClassName="no-wrap inspection-checkbox-label"
            ClassName=""
            checked={showCompleted}
            onCheckBoxChange={(name: string, isChecked: boolean) => {
              if (onShowCompletedChange) {
                onShowCompletedChange(isChecked);
              }
            }}
          />
        </Grid>
      </Grid>

      <div className="inspection-overview-sort-filter">
        <label htmlFor="button label">Sort by: Planned Completion Date</label>
        <Button
          className="rounded-btn mr-10"
          variant="outlined"
          title={getSortTooltipText()}
          startIcon={
            <div
              style={{
                transition: "transform 0.3s ease",
                display: "flex",
                alignItems: "center",
              }}
            >
              {getSortStateIcon()}
            </div>
          }
          style={{
            height: "fit-content",
            transition: "all 0.2s ease",
          }}
          onClick={handlePlannedCompletionDateSort}
        />

        <Button
          className="rem-lined-btn"
          variant="outlined"
          startIcon={<Filter variant="Outline" size={20} />}
          style={{ height: "fit-content" }}
          onClick={handleClick}
        >
          Location Filter
        </Button>

        {/* Location Filter Popover */}
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <Box
            sx={{
              width: "320px",
              height: "auto",
              padding: "16px",
            }}
          >
            {/* Select All / Deselect All Buttons */}
            <Stack
              direction="row"
              className="flex--container space-between pt-10 pb-10"
            >
              <Button
                onClick={onSelectAllClick}
                sx={{
                  color: "#0277bd",
                  textTransform: "none",
                  fontSize: "14px",
                  padding: "0",
                }}
              >
                Select All
              </Button>
              <Button
                onClick={onDeselectAllClick}
                sx={{
                  color: "#0277bd",
                  textTransform: "none",
                  fontSize: "14px",
                  padding: "0",
                }}
              >
                Deselect All
              </Button>
            </Stack>
            {/* Search Bar */}
            <div>
              <Paper
                component="form"
                sx={{
                  p: "2px 4px",
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  border: "1px solid #e0e0e0",
                  borderRadius: "25px",
                  height: "35px",
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <InputBase
                  value={searchTerm}
                  onChange={(event) => {
                    onChangeSearchName(event.target?.value);
                  }}
                  onKeyPress={onKeyPressSearchName}
                  sx={{ ml: 1, flex: 1 }}
                  placeholder="Search Location(s)"
                  inputProps={{ "aria-label": "search" }}
                />
                {isMouseOver && searchTerm && searchTerm.length > 0 && (
                  <Tooltip title="Clear" placement="top">
                    <IconButton
                      className="mr-0"
                      onClick={() => onChangeSearchName("")}
                    >
                      <CloseIcon
                        style={{ color: "#219ebc", fontSize: "20px" }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Search" placement="top">
                  <IconButton
                    type="button"
                    sx={{ p: "10px" }}
                    aria-label="search"
                    className="m-0"
                    onClick={onClickSearchBtn}
                  >
                    <SearchNormal1
                      variant="Outline"
                      className="ico-secondary"
                      size={18}
                    />
                  </IconButton>
                </Tooltip>
              </Paper>
            </div>
            {/* Location List */}
            <FormGroup>
              <TooltipScrollContainer
                className="location-list"
                style={{
                  maxHeight: "400px",
                  overflowY: "auto",
                  borderRadius: "4px",
                }}
              >
                {filteredLocations.map((loc) => (
                  <div key={loc.Id} className="location-item mb-5">
                    <HPRCheckBox
                      Id={"location_" + loc.Id}
                      Name={"location_" + loc.Id}
                      Label={loc.SiteName}
                      LabelClassName="no-wrap inspection-location-label"
                      showLabelTooltip={true}
                      ClassName=""
                      checked={checkIfLocationIsSelected(loc.Id)}
                      onCheckBoxChange={(name: string, isChecked: boolean) => {
                        handleLocationSelection(loc.Id, isChecked);
                      }}
                    />
                  </div>
                ))}

                {filteredLocations.length === 0 && (
                  <div className="gray-text body3 text-center py-20">
                    No locations found
                  </div>
                )}
              </TooltipScrollContainer>
            </FormGroup>
            <div
              className="inspection-btn-section"
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <Button
                className="rem-lined-btn"
                variant="outlined"
                style={{
                  height: "fit-content",
                  visibility: "hidden",
                  fontSize: "0.875rem",
                }}
              >
                Export
              </Button>
              <Button
                className="rem-lined-btn mr-10 "
                variant="outlined"
                style={{ height: "30px", fontSize: "0.875rem" }}
                onClick={() => {
                  if (onApplyLocationFilters) {
                    onApplyLocationFilters(selectedLocationIds);
                  }
                }}
              >
                Apply
              </Button>
            </div>{" "}
          </Box>
        </Popover>
      </div>
    </div>
  );
};

export default InspectionOverviewFilterSection;
